/* ==========================================================================
   Akel Beauty — cart drawer

   Every mutation goes through Shopify: /cart/change.js applies the change, then
   the drawer's markup is re-fetched via the Section Rendering API. Money,
   discounts and line-item properties are therefore always Shopify's, never
   recomputed here — which matters most for the final-sale notice, since that
   has to survive every quantity change.

   /cart remains a working page. If this script fails, links still go there.
   ========================================================================== */

(function () {
  'use strict';

  var SECTION_ID = 'cart-drawer';

  function init() {
    var drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    var panel = drawer.querySelector('[data-cart-panel]');
    var lastFocused = null;
    var busy = false;

    /* ------------------------------------------------------------ Open/close */

    function open() {
      lastFocused = document.activeElement;
      drawer.hidden = false;
      // Force a reflow so the transition has a start state, then add the class
      // synchronously. requestAnimationFrame is throttled in backgrounded or
      // non-rendering tabs — and when it never fires the panel stays
      // translateX(100%), i.e. open but invisible. This cannot fail that way.
      void drawer.offsetWidth;
      drawer.classList.add('is-open');
      document.documentElement.classList.add('drawer-open');
      var focusable = panel.querySelector('button, a, input');
      if (focusable) focusable.focus();
    }

    function close() {
      drawer.classList.remove('is-open');
      document.documentElement.classList.remove('drawer-open');
      var done = function () {
        drawer.hidden = true;
        drawer.removeEventListener('transitionend', done);
      };
      drawer.addEventListener('transitionend', done);
      // Belt and braces if the transition never fires (reduced motion).
      window.setTimeout(function () {
        if (!drawer.classList.contains('is-open')) drawer.hidden = true;
      }, 400);
      if (lastFocused) lastFocused.focus();
    }

    // Keep focus inside the panel while it is open.
    document.addEventListener('keydown', function (event) {
      if (drawer.hidden) return;
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      var items = panel.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    /* ------------------------------------------------- Re-render from Shopify */

    function refresh() {
      return fetch(window.Akel.routes.root_url + '?sections=' + SECTION_ID, {
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Cart drawer refresh failed');
          return response.json();
        })
        .then(function (sections) {
          var markup = sections[SECTION_ID];
          if (!markup) throw new Error('Cart drawer section missing');
          var parsed = new DOMParser().parseFromString(markup, 'text/html');
          var fresh = parsed.querySelector('[data-cart-drawer]');
          if (!fresh) throw new Error('Cart drawer markup missing');

          var wasOpen = drawer.classList.contains('is-open');
          drawer.innerHTML = fresh.innerHTML;
          panel = drawer.querySelector('[data-cart-panel]');
          bind();
          if (wasOpen) drawer.classList.add('is-open');

          // Header badge follows the drawer's own count.
          var count = drawer.querySelector('[data-cart-count]');
          if (count) {
            document.querySelectorAll('.cart-count').forEach(function (node) {
              node.textContent = count.textContent.trim();
            });
          }
        });
    }

    /* ------------------------------------------------------ Quantity changes */

    function changeLine(line, quantity) {
      if (busy) return;
      busy = true;
      panel.classList.add('is-busy');

      fetch(window.Akel.routes.cart_change_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      })
        .then(function (response) {
          return response.json();
        })
        .then(function () {
          return refresh();
        })
        .catch(function () {
          // Cart state is uncertain — hand over to the real page rather than
          // showing something possibly wrong.
          window.location.href = window.Akel.routes.cart_url;
        })
        .finally(function () {
          busy = false;
          if (panel) panel.classList.remove('is-busy');
        });
    }

    /* --------------------------------------------------------------- Binding */

    function bind() {
      drawer.querySelectorAll('[data-cart-close]').forEach(function (el) {
        el.addEventListener('click', function (event) {
          // Links inside the drawer should still navigate.
          if (el.tagName === 'A' && el.getAttribute('href')) return;
          event.preventDefault();
          close();
        });
      });

      drawer.querySelectorAll('[data-cart-qty]').forEach(function (button) {
        button.addEventListener('click', function () {
          changeLine(
            parseInt(button.getAttribute('data-cart-qty'), 10),
            parseInt(button.getAttribute('data-cart-qty-value'), 10)
          );
        });
      });
    }

    bind();

    /* --------------------------------------------------------- Entry points */

    // Header cart link opens the drawer instead of navigating.
    document.querySelectorAll('[data-cart-link]').forEach(function (link) {
      link.addEventListener('click', function (event) {
        event.preventDefault();
        refresh().then(open).catch(function () {
          window.location.href = window.Akel.routes.cart_url;
        });
      });
    });

    // The product form dispatches this after a successful add.
    document.addEventListener('akel:cart:updated', function () {
      refresh().then(open).catch(function () {
        window.location.href = window.Akel.routes.cart_url;
      });
    });

    // An accessory added from an already-open drawer should not throw the
    // keyboard user back to the close button. Re-render authoritatively, then
    // continue with the next eligible add-on (or checkout when none remain).
    document.addEventListener('akel:cart:upsell-added', function (event) {
      var requestedIndex = event.detail && Number.isInteger(event.detail.upsellIndex)
        ? event.detail.upsellIndex
        : 0;
      refresh()
        .then(function () {
          if (!drawer.classList.contains('is-open')) {
            open();
            return;
          }
          var remainingActions = drawer.querySelectorAll('[data-cart-upsell-action]');
          var nextAction = remainingActions[requestedIndex] || drawer.querySelector('.drawer__checkout');
          if (nextAction) nextAction.focus();
        })
        .catch(function () {
          // The add already succeeded. Never invite a duplicate retry against
          // stale markup; hand over to Shopify's authoritative cart page.
          window.location.href = window.Akel.routes.cart_url;
        });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
